// TypeScript function
export interface HttpStatus {
  code: number;
  message: string;
}

interface RequestResponse {
  statusCode: number;
  response: Object;
  error: any;
}

export const httpStatusCodes = [
  {
    code: 200,
    message: "La solicitud ha tenido éxito.",
    title: "OK"
  },
  {
    code: 201,
    message: "La solicitud ha sido completada y se ha creado un nuevo recurso.",
    title: "Creado"
  },
  {
    code: 204,
    message: "La solicitud se ha completado con éxito, pero no hay contenido para devolver.",
    title: "Sin contenido"
  },
  {
    code: 400,
    message: "La solicitud es invalida, contenido de la solicitud invalido.",
    title: "Solicitud inválida"
  },
  {
    code: 401,
    message: " No tienes los permisos suficientes o debes volver a reautenticarte.",
    title: "No autorizado"
  },
  {
    code: 403,
    message: "La solicitud requiere reautenticación del usuario, pero el usuario no ha proporcionado credenciales válidas",
    title: "Prohibido"
  },
  {
    code: 404,
    message: "El recurso solicitado no se pudo encontrar en el servidor.",
    title: "No encontrado"
  },
  {
    code: 500,
    message: "Hubo un error al procesar su solicitud. Es posible que no tengas suficientes permisos para ejecutar esta acción.",
    title: "Error interno del servidor"
  },
  {
    code: 502,
    message: "El servidor estaba actuando como puerta de enlace o proxy y recibió una respuesta incorrecta del servidor ascendente.",
    title: "Puerta de enlace incorrecta"
  }
];

export const successHttpStatusCodes = [200, 201, 204];

export const isHttpCodeSuccess = (statusCode: number): boolean =>
  successHttpStatusCodes.includes(statusCode);

export const findHttpStatusInfo = (statusCode: number): any =>
  httpStatusCodes.find((s) => s.code === statusCode);

export const httpErrorCategories = {
  Informational: [100, 101, 102],
  Successful: [200, 201, 202, 203, 204, 205, 206, 207, 208, 226],
  Redirection: [300, 301, 302, 303, 304, 305, 306, 307, 308],
  ClientError: [
    400, 401, 402, 403, 404, 405, 406, 407, 408, 409, 410, 411, 412, 413, 414,
    415, 416, 417, 418, 421, 422, 423, 424, 426, 428, 429, 431, 451,
  ],
  ServerError: [500, 501, 502, 503, 504, 505, 506, 507, 508, 510, 511],
};

export const handleSwalMessage = (
  responseData: RequestResponse,
  message: string | undefined
): object => {
  const { statusCode, error } = responseData;
  const icon = isHttpCodeSuccess(statusCode) ? 'success' : 'error';
  const title = isHttpCodeSuccess(statusCode) ? 'Éxito' : `(status: ${statusCode})`;
  
  return { icon, title, text: error || message }
};

export const getHttpErrorCategory = (status: number) => {
  const [category] =
    Object.entries(httpErrorCategories).find(([_, codes]) =>
      codes.includes(status)
    ) || [];
  return category || "UnknownError";
};

export const replaceAt = (array: any, index: number, value: any) => {
  return [...array.slice(0, index), value, ...array.slice(index + 1)];
};

export const isNotEmpty = (val: unknown): boolean => {
  if (val == null) {
    return false;
  } else if (typeof val === "string" || Array.isArray(val)) {
    return val.length > 0;
  } else if (typeof val === "object") {
    return Object.keys(val).length > 0;
  } else {
    return true;
  }
};

export const rejectEmptyKeysOrValues = (
  arr: Record<string, unknown>[]
): Record<string, unknown>[] => {
  return arr.filter((obj: Record<string, unknown>) => {
    return Object.entries(obj).every(([key, value]) => {
      return isNotEmpty(key) && isNotEmpty(value);
    });
  });
};

export const cleanString = (str: string): string => {
  return str.replace(/[^a-zA-Z0-9-_./]/g, "").trim();
};
